import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { FsNavData, FsStatus } from '../../../../domain/financial-statement/utils/common/utilityClasses';
import { SharedComponentsModule } from '../../../../domain/shared/module/shared-components.module';
import { HorNavBarShowElements, HorNavBarShowPicker } from '../dto/HorNavBarShowElements';
import { IsHorNav } from '../horizontal-nav-bar.component';
import { FundPerformance } from '../../../../domain/fund-performance/dto/FundPerformance';
import { FsSelectorPeriod } from '../../../../domain/financial-statement/utils/common/FsSelectorPeriod';
import { FsNavigationService } from '../../../../domain/financial-statement/services/fs-nav/fs-navigation.service';
import { ToolTipSettings } from '../../../../domain/shared/component/tooltip/tooltip.util';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Organization } from '../../../../domain/organization/dto/Organization';
import { HorNavbarService } from '../../../service/hor-navbar.service';
import {
  FundPerformanceBackendService,
} from '../../../../domain/fund-performance/service/fund-performance-backend.service';
import {
  FsGettersSettersService,
} from '../../../../domain/financial-statement/services/common/fs-getters-setters.service';
import {
  getFundPerfTooltipText,
} from "../../../../domain/fund-performance/component/fund-performances/pojo/fp-export-classes";
import { AuthService, UserType } from "../../../../domain/auth/service/auth.service";
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../../service/delay.service";

@Component({
  selector: 'app-period-picker',
  templateUrl: './period-picker.component.html',
  styleUrls: ['./period-picker.component.scss'],
  standalone: true,
  animations: [fadeInOutAnimation(200, 0)],
  imports: [
    SharedComponentsModule,
  ],
  providers: [DelayService],
})
export class PeriodPickerComponent implements AfterViewInit {
  @Input() showElements: HorNavBarShowElements;
  @Input() filteredFundPerformances: FundPerformance[];
  @Input() isMultiAccess: boolean;
  @Input() isManagementEntity: boolean;
  @Input() selectedFundPerformance: FundPerformance;
  @Input() currentService: string;
  @Input() fsNavData: FsNavData = new FsNavData();
  @Input() selectedOrganization: Organization;
  @Output() fundPerformanceChange = new EventEmitter<FundPerformance>();
  @Output() actionEmitter = new EventEmitter<PERIOD_PICKER_ACTION>();


  userType: UserType = 'viewer';
  showPicker: HorNavBarShowPicker = new HorNavBarShowPicker();
  is: IsHorNav = new IsHorNav();
  toolTipSettings: ToolTipSettings = {
    show: false,
    title: '',
  };

  protected readonly FsStatus = FsStatus;
  protected readonly ACTION_FLAG = PERIOD_PICKER_ACTION;

  private _destroyStream: Subject<void> = new Subject<void>();
  private _fsDestroyStream: Subject<void> = new Subject<void>();

  constructor(
    private fsNavigationService: FsNavigationService,
    private navbarService: HorNavbarService,
    private fundPerformanceService: FundPerformanceBackendService,
    private fsGetterSetter: FsGettersSettersService,
    private authService: AuthService,
    private delayService: DelayService,
  ) {
    this.initFsSubscriptions();
    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.userType = userType;
      },
      );
  }

  ngAfterViewInit(): void {
    this.delayService.initSubscribes(DEFAULT_DROP_DOWN_DELAY_TIME, this.showDropDown, this.hideDropDown);
  }

  activateDropDown(): void {
    this.delayService.activate();
  }

  deactivateDropDown(): void {
    this.delayService.deactivate();
  }

  switchFp(fp: FundPerformance): void {
    this.fundPerformanceChange.emit(fp);
  }

  activateTooltip(id: string, fundPerformance?: FundPerformance): void {
    this.toolTipSettings.title = getFundPerfTooltipText(id, fundPerformance);
    this.toolTipSettings.show = true;
    this.toolTipSettings.coordinates = document.getElementById(id).getBoundingClientRect();
  }

  hideTooltip(): void {
    this.toolTipSettings.show = false;
  }

  async toggleIsPeriodPredicted(fp: FundPerformance): Promise<void> {
    if (this.userType === 'admin') {
      this.hideTooltip();
      fp.isBasedOnPrognosis = !fp.isBasedOnPrognosis;
      const updatedFp = await this.fundPerformanceService.update(fp, false);
      this.navbarService.updateFundPerformance(updatedFp);
    }
  }

  /**
   * Updates {@link FundPerformance#isVisible} status,
   * prevents hiding if fp has a published Investor report({@link FundPerformance#isPublished}).
   */
  async togglePeriodVision(fp: FundPerformance): Promise<void> {
    if (fp.isPublished && fp.isVisible) {
      return;
    }
    this.hideTooltip();
    fp = await firstValueFrom(this.fundPerformanceService.changeVisibility(
      this.selectedOrganization.selectedFund.uuid, fp.period, !fp.isVisible),
    );
    this.navbarService.updateFundPerformance(fp);
  }

  switchFsPeriod(fsPeriod: FsSelectorPeriod): void {
    void this.fsNavigationService.periodChange(fsPeriod);
    const selectedFp = this.filteredFundPerformances.find((fp) => fp.period === fsPeriod.period);
    if (selectedFp) {
      void this.switchFp(selectedFp);
    }
  }

  showCreatePeriodModal(): void {
    this.fsGetterSetter.setShowCreatePeriodModal(true);
  }

  copyFsPeriod(): void {
    this.fsNavigationService.findPeriodsForCopy();
    this.fsGetterSetter.setShowCopyPeriodModal(true);
  }

  deleteFsPeriodData(): void {
    this.fsGetterSetter.setShowDeleteModal(true);
  }

  deleteFsPeriod(period: FsSelectorPeriod): void {
    void this.fsNavigationService.deletePeriod(period, this.fsGetterSetter.selectedOwnershipEntity.value.uuid);
  }

  changeFsStatus(f: FsSelectorPeriod): void {
    if (f.period !== this.fsNavData.selectedPeriod.period || this.userType !== 'admin') {
      return;
    }
    f.status = (f.status === FsStatus.started) ? FsStatus.completed : FsStatus.started;
    this.fsNavData.isCompleted = f.status === FsStatus.completed;
    this.fsGetterSetter.setSelectedEntityPeriod(f);
    this.fsNavigationService.changeStatus(this.fsNavData.isCompleted);
  }

  private initFsSubscriptions(): void {
    this.fsGetterSetter.ownershipEntityPeriods
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((fsPeriodsWithStatus: FsSelectorPeriod[]): void => {
        this.fsNavData.periods = fsPeriodsWithStatus;
      });
    this.fsGetterSetter.selectedEntityPeriod
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((selectedPeriod: FsSelectorPeriod): void => {
        this.fsNavData.selectedPeriod = selectedPeriod;
      });
  }

  /**
   There are two HTML divs for the period selector.
   Showing a specific picker depends on the current service.
   On hover over period picker, this function dictates which of the pickers is shown
   (e.x. we are in the portfolio performance, then the current service is 'FMS' => show the picker for 'FMS')
   **/
  private checkPicker(isMouseEnter: boolean): void {
    switch (this.currentService) {
      case 'FMS':
        this.showPicker.fp.isShown = isMouseEnter;
        break;
      case 'FS':
        this.showPicker.fs.isShown = isMouseEnter;
        break;
    }
  }

  private showDropDown: () => void = () => {
    this.checkPicker(true);
  };

  private hideDropDown: () => void = () => {
    this.checkPicker(false);
  };
}

export enum PERIOD_PICKER_ACTION {
  DELETE = 'delete',
  COPY = 'copy',
  BLOCK = 'block',
}
